/**
 * Базовый класс типа узла ArchiMate для X6.
 * Каждый вид элемента/связи описывается отдельным классом-наследником:
 * свойства фигуры, правила вложенности и соединения.
 */
import {
	getRelationshipsAllowed,
	getReverseRelationshipsAllowed,
} from '../features/popup-menu/ConnectionOptions';
import {textAlignToLabelAttrs} from './textAlignUtil';

export default class ArchimateNodeType {
	/**
	 * Имя типа элемента (для модели и FULL_ELEMENT_MAP).
	 * @type {string}
	 */
	static get typeName() {
		return 'Element';
	}

	/**
	 * Идентификатор формы X6 (например 'archimate:application-component').
	 * @returns {string}
	 */
	get shapeId() {
		return `archimate:${this._toKebabCase(this.constructor.typeName)}`;
	}

	/**
	 * Размер по умолчанию { width, height }.
	 * @returns {{ width: number, height: number }}
	 */
	get defaultSize() {
		return {width: 120, height: 55};
	}

	/**
	 * Может ли узел этого типа быть родителем (контейнером) для вложенных элементов.
	 * По умолчанию true; для Junction и Note — false.
	 */
	get canBeParent() {
		return true;
	}

	/**
	 * Можно ли соединять данный тип узла с целевым типом (по правилам ArchiMate).
	 * @param {string} targetTypeName — тип целевого узла
	 * @returns {boolean}
	 */
	canConnectTo(targetTypeName) {
		const sourceShape = {type: this.constructor.typeName, incoming: [], outgoing: []};
		const targetShape = {type: targetTypeName, incoming: [], outgoing: []};
		const direct = getRelationshipsAllowed(sourceShape, targetShape);
		const reverse = getReverseRelationshipsAllowed(sourceShape, targetShape);
		return direct.length > 0 || reverse.length > 0;
	}

	/**
	 * Список допустимых типов связей к целевому типу (для контекстного меню).
	 * @param {string} targetTypeName
	 * @returns {Array<{ type: string, reverse?: boolean, label: string }>}
	 */
	getAllowedRelationshipTypes(targetTypeName) {
		const sourceShape = {type: this.constructor.typeName, incoming: [], outgoing: []};
		const targetShape = {type: targetTypeName, incoming: [], outgoing: []};
		const direct = getRelationshipsAllowed(sourceShape, targetShape);
		const reverse = getReverseRelationshipsAllowed(sourceShape, targetShape);
		const result = [];
		direct.forEach(entry => {
			const t = entry?.target;
			result.push({
				type: t?.type ?? entry?.label ?? 'Association',
				reverse: false,
				label: entry?.label ?? t?.type ?? 'Relationship',
			});
		});
		reverse.forEach(entry => {
			const t = entry?.target;
			result.push({
				type: t?.type ?? entry?.label ?? 'Association',
				reverse: true,
				label: entry?.label ?? t?.type ?? 'Relationship',
			});
		});
		return result;
	}

	/**
	 * Сборка конфига узла для X6 (id, x, y, width, height, shape, attrs, data).
	 * Переопределяется в наследниках.
	 * @param {Object} pathMap — PathMap для генерации path иконок
	 * @param {Object} options — { id, x, y, width, height, label, fillColor?, lineColor? }
	 * @returns {Object} nodeConfig для graph.addNode()
	 */
	buildNodeConfig(pathMap, options = {}) {
		const {id, x = 0, y = 0, width, height, label, textAlign} = options;
		const size = this.defaultSize;
		const w = width ?? size.width;
		const h = height ?? size.height;
		const align = textAlignToLabelAttrs(textAlign);
		return {
			id,
			x,
			y,
			width: w,
			height: h,
			shape: this.shapeId,
			attrs: {
				label:
					label != null
						? {
								text: label,
								...align,
								textWrap: {width: '100%', height: '100%', ellipsis: true},
							}
						: undefined,
			},
		};
	}

	_toKebabCase(value) {
		return String(value)
			.replace(/([a-z0-9])([A-Z])/g, '$1-$2')
			.replace(/\s+/g, '-')
			.toLowerCase();
	}
}
